/**
 * Indicador de Inboxes Desconectadas no Header
 * Exibe ícone com badge quando há inboxes com problemas de conexão
 * Popover mostra lista das inboxes e permite ação de reconexão
 * 
 * v1.1.0 - 2026-02-01: Suporte a modal específico para Instagram
 */
import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { WifiOff, AlertTriangle, ArrowRight, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useAuth } from '@/hooks/useAuth';
import { useDisconnectedInboxes, DisconnectedInbox } from '@/hooks/useDisconnectedInboxes';
import { QRCodeConnectionDialog } from './QRCodeConnectionDialog';
import { InstagramReconnectDialog } from './InstagramReconnectDialog';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { createLogger } from '@/lib/logger';

/**
 * Providers que suportam reconexão via QR Code
 */
const QR_CODE_PROVIDERS = ['z-api', 'waha'];

/**
 * Providers Meta que usam modal específico de credenciais
 */
const META_INSTAGRAM_PROVIDER = 'meta-instagram';

/**
 * Componente do indicador de inboxes desconectadas
 * Só renderiza quando há pelo menos 1 inbox com problema
 */
export function DisconnectedInboxesIndicator() {
  const navigate = useNavigate();
  const { currentTenant } = useAuth();
  const { data: disconnectedInboxes = [], isLoading } = useDisconnectedInboxes(currentTenant?.id);
  
  // Estado para popover
  const [popoverOpen, setPopoverOpen] = useState(false);
  
  // Estado para dialog de reconexão QR Code (WhatsApp)
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [selectedQrInbox, setSelectedQrInbox] = useState<DisconnectedInbox | null>(null);
  
  // Estado para dialog de reconexão Instagram
  const [instagramDialogOpen, setInstagramDialogOpen] = useState(false);
  const [selectedInstagramInbox, setSelectedInstagramInbox] = useState<DisconnectedInbox | null>(null);

  // Logger para operações do indicador (deve estar antes de qualquer early return)
  const indicatorLogger = createLogger('DisconnectedIndicator');

  /**
   * Ativa a inbox selecionada quando está inativa
   * Usado pelo QRCodeConnectionDialog quando detecta statusProtected
   * NOTA: Deve estar definido antes do early return para evitar erro de hooks
   */
  const handleActivateInbox = useCallback(async (): Promise<void> => {
    if (!selectedQrInbox) {
      throw new Error('Nenhuma inbox selecionada para ativação');
    }

    indicatorLogger.info('Ativando inbox via indicador de desconectadas', { 
      inboxId: selectedQrInbox.id 
    });

    const { error } = await supabase
      .from('inboxes')
      .update({ status: 'active' })
      .eq('id', selectedQrInbox.id);

    if (error) {
      indicatorLogger.error('Erro ao ativar inbox', error);
      toast.error('Erro ao ativar inbox');
      throw error;
    }

    toast.success('Inbox ativada!');
    indicatorLogger.info('Inbox ativada com sucesso', { inboxId: selectedQrInbox.id });
  }, [selectedQrInbox, indicatorLogger]);

  // Não renderiza nada se não houver inboxes desconectadas
  if (isLoading || disconnectedInboxes.length === 0) {
    return null;
  }

  const count = disconnectedInboxes.length;

  /**
   * Abre o modal apropriado ou navega para detalhes dependendo do provider
   * 
   * - z-api, waha: QR Code dialog
   * - meta-instagram: Instagram Reconnect dialog (credenciais + teste)
   * - outros: Navega para página de detalhes (fallback)
   */
  const handleReconnect = (inbox: DisconnectedInbox) => {
    setPopoverOpen(false);
    
    const providerSlug = inbox.provider_slug;
    
    // Providers com QR Code (WhatsApp Z-API/WAHA)
    if (providerSlug && QR_CODE_PROVIDERS.includes(providerSlug)) {
      setSelectedQrInbox(inbox);
      setQrDialogOpen(true);
      return;
    }
    
    // Instagram - modal específico com credenciais e teste
    if (providerSlug === META_INSTAGRAM_PROVIDER) {
      setSelectedInstagramInbox(inbox);
      setInstagramDialogOpen(true);
      return;
    }
    
    // Fallback: navega para página de detalhes da inbox
    navigate(`/tenant/inboxes/${inbox.id}`);
  };

  /**
   * Navega para a página de inboxes
   */
  const handleViewAll = () => {
    setPopoverOpen(false);
    navigate('/tenant/inboxes');
  };

  /**
   * Callback quando a inbox é reconectada com sucesso via QR Code
   */
  const handleQrConnected = () => {
    setQrDialogOpen(false);
    setSelectedQrInbox(null);
  };

  /**
   * Callback quando a inbox Instagram é reconectada com sucesso
   */
  const handleInstagramConnected = () => {
    setInstagramDialogOpen(false);
    setSelectedInstagramInbox(null);
  };

  return (
    <>
      <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="relative h-9 w-9 rounded-full hover:bg-destructive/10"
              >
                <WifiOff className="h-4 w-4 text-destructive" />
                {/* Badge com contador */}
                <Badge
                  variant="destructive"
                  className="absolute -top-1 -right-1 h-5 w-5 p-0 flex items-center justify-center text-[10px] font-bold"
                >
                  {count > 9 ? '9+' : count}
                </Badge>
              </Button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent side="bottom">
            <p>{count} inbox{count > 1 ? 'es' : ''} desconectada{count > 1 ? 's' : ''}</p>
          </TooltipContent>
        </Tooltip>

        <PopoverContent className="w-80 p-0" align="end" sideOffset={8}>
          {/* Header do Popover - v1.0.2: Título genérico para incluir connecting */}
          <div className="flex items-center gap-2 p-3 border-b bg-destructive/5">
            <AlertTriangle className="h-4 w-4 text-destructive" />
            <span className="text-sm font-medium text-destructive">
              Inboxes com Problemas
            </span>
          </div>

          {/* Lista de Inboxes */}
          <div className="max-h-64 overflow-y-auto">
            {disconnectedInboxes.map((inbox) => (
              <div
                key={inbox.id}
                className="flex items-center justify-between p-3 border-b last:border-b-0 hover:bg-muted/50 transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{inbox.name}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-xs text-muted-foreground">
                      {inbox.provider_name || inbox.channel_type}
                    </span>
                    {/* v1.0.2: Adicionado estilo para 'connecting' (azul) */}
                    <Badge
                      variant="outline"
                      className={cn(
                        'text-[10px] px-1.5 py-0',
                        inbox.status === 'error'
                          ? 'border-destructive/50 text-destructive'
                          : inbox.status === 'connecting'
                            ? 'border-blue-500/50 text-blue-600 dark:text-blue-400'
                            : 'border-amber-500/50 text-amber-600 dark:text-amber-400'
                      )}
                    >
                      {inbox.status === 'error' 
                        ? 'Erro' 
                        : inbox.status === 'connecting' 
                          ? 'Conectando...' 
                          : 'Desconectado'}
                    </Badge>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-xs"
                  onClick={() => handleReconnect(inbox)}
                >
                  <RefreshCw className="h-3 w-3 mr-1" />
                  Reconectar
                </Button>
              </div>
            ))}
          </div>

          {/* Footer com link para página de inboxes */}
          <div className="p-2 border-t bg-muted/30">
            <Button
              variant="ghost"
              size="sm"
              className="w-full justify-between text-xs"
              onClick={handleViewAll}
            >
              Ver todas as inboxes
              <ArrowRight className="h-3 w-3" />
            </Button>
          </div>
        </PopoverContent>
      </Popover>

      {/* Dialog de Reconexão QR Code (WhatsApp Z-API/WAHA) */}
      {selectedQrInbox && (
        <QRCodeConnectionDialog
          open={qrDialogOpen}
          onOpenChange={setQrDialogOpen}
          inboxId={selectedQrInbox.id}
          inboxName={selectedQrInbox.name}
          onConnected={handleQrConnected}
          providerSlug={selectedQrInbox.provider_slug || undefined}
          onActivateInbox={handleActivateInbox}
        />
      )}

      {/* Dialog de Reconexão Instagram (Meta) */}
      {selectedInstagramInbox && (
        <InstagramReconnectDialog
          open={instagramDialogOpen}
          onOpenChange={setInstagramDialogOpen}
          inboxId={selectedInstagramInbox.id}
          inboxName={selectedInstagramInbox.name}
          initialStatus={selectedInstagramInbox.status}
          onConnected={handleInstagramConnected}
        />
      )}
    </>
  );
}
